import { useState } from "react";
import axios from "../../../axios";
import { toast } from "react-toastify";

const STATUS_OPTIONS = ["draft", "published", "live", "co"];

const initialForm = {
  name: "",
  description: "",
  lectureTime: "",
  status: "draft",
  videoUrl: "",
  thumbnailUrl: "",
  durationMinutes: "",
  subject: "",
  batch: "",
  tags: "",
};

const CreateLecture = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      videoUrl: form.videoUrl.trim(),
    };

    if (form.description.trim()) {
      payload.description = form.description.trim();
    }

    if (form.lectureTime) {
      payload.lectureTime = new Date(form.lectureTime).toISOString();
    }

    if (form.status) {
      payload.status = form.status;
    }

    if (form.thumbnailUrl.trim()) {
      payload.thumbnailUrl = form.thumbnailUrl.trim();
    }

    if (form.durationMinutes !== "") {
      payload.durationMinutes = Number(form.durationMinutes);
    }

    if (form.subject.trim()) {
      payload.subject = form.subject.trim();
    }

    if (form.batch.trim()) {
      payload.batch = form.batch.trim();
    }

    const tags = form.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    if (tags.length) {
      payload.tags = tags;
    }

    return payload;
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error("Lecture name is required");
      return;
    }

    if (!form.videoUrl.trim()) {
      toast.error("Video URL is required");
      return;
    }

    setLoading(true);

    try {
      await axios.post("/institute/lectures", buildPayload());
      toast.success("Lecture created successfully");
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Lecture create nahi ho paya");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bgWhite px-4 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5">
          <h2 className="font-oswald text-2xl font-bold text-purple">
            Create Lecture
          </h2>
          <p className="text-sm text-gray-600">
            Institute ya library account ke token se lecture create karein.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Lecture Name
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="name"
                placeholder="Algebra Basics"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Video URL
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="videoUrl"
                placeholder="https://example.com/videos/algebra-basics.mp4"
                value={form.videoUrl}
                onChange={handleChange}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                className="min-h-24 w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="description"
                placeholder="Linear equations aur basic algebra concepts ka lecture"
                value={form.description}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Lecture Time
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="lectureTime"
                type="datetime-local"
                value={form.lectureTime}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Thumbnail URL
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="thumbnailUrl"
                placeholder="https://example.com/images/algebra-thumb.jpg"
                value={form.thumbnailUrl}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Duration Minutes
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                min="1"
                name="durationMinutes"
                type="number"
                placeholder="45"
                value={form.durationMinutes}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="subject"
                placeholder="Mathematics"
                value={form.subject}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Batch
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="batch"
                placeholder="Morning Batch"
                value={form.batch}
                onChange={handleChange}
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Tags
              </label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-purple"
                name="tags"
                placeholder="algebra, maths, class-10"
                value={form.tags}
                onChange={handleChange}
              />
            </div>

            <div className="flex gap-3 md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="rounded bg-purple px-5 py-2 font-semibold text-white disabled:opacity-70"
              >
                {loading ? "Creating..." : "Create Lecture"}
              </button>
              <button
                type="button"
                className="rounded border border-gray-300 px-5 py-2 font-semibold text-gray-700"
                onClick={resetForm}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateLecture;
